import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, MessagesSquare, Heart, Users, ArrowRight, Crown } from "lucide-react";

const REASONS = [
  {
    icon: Heart,
    label: "감정적 연결",
    desc: "단순한 매칭이 아닌, 마음과 마음이 만나는 진솔한 대화.",
  },
  {
    icon: MessagesSquare,
    label: "진짜 이야기",
    desc: "삶의 깊이가 묻어나는 글, 깊이 있는 댓글 문화.",
  },
  {
    icon: Crown,
    label: "프리미엄 중년",
    desc: "직장·가정·인생 2막을 함께 고민하는 성숙한 회원층.",
  },
  {
    icon: ShieldCheck,
    label: "안전한 환경",
    desc: "본인인증 · 사진 인증 · 동의 4단계로 신뢰를 지킵니다.",
  },
];

export const WhyDifferentSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="why-different-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Text */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[12px] tracking-[0.3em] font-semibold text-amber-700 mb-4">
                WHY DIFFERENT
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.15] tracking-tight">
                중년의 삶을
                <br />
                존중하는 공간
              </h2>
              <p className="text-stone-600 text-[15px] leading-[1.85] mt-6">
                젊은 매칭의 가벼움이 아닌, 우리 나이만의 진중한 만남.
                <br />
                감정과 삶의 깊이를 이해하는 사람들이 모입니다.
              </p>
              <Link
                to="/community"
                className="inline-flex items-center gap-2 mt-8 text-burgundy-700 font-semibold text-sm hover:gap-3 transition-all"
                data-testid="why-different-cta"
              >
                바로 시작하기 <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>

          {/* Right: Grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {REASONS.map((r, i) => {
                const Icon = r.icon;
                return (
                  <motion.div
                    key={r.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="bg-[#FAF8F4] rounded-3xl p-6 border border-stone-100 hover:border-stone-200 transition-colors"
                  >
                    <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center mb-4 shadow-sm border border-stone-100">
                      <Icon className="w-5 h-5 text-burgundy-600" strokeWidth={1.7} />
                    </div>
                    <h3 className="font-serif text-lg text-stone-900 mb-2 tracking-tight">{r.label}</h3>
                    <p className="text-[13.5px] text-stone-600 leading-[1.75]">{r.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Final CTA — 가입 유도
 */
export const FinalCTASection = () => {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden bg-stone-900 text-white" data-testid="final-cta-section">
      <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-800 to-burgundy-900/50 pointer-events-none" />
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] rounded-full bg-rose-500/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[30rem] h-[30rem] rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 text-[12px] tracking-[0.3em] font-semibold text-amber-300 mb-6">
            <Users className="w-3.5 h-3.5" />
            JOIN THE COMMUNITY
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.15] tracking-tight">
            오늘, 첫 이야기를
            <br />
            들려주실래요?
          </h2>
          <p className="text-stone-300 text-[15px] sm:text-base leading-[1.85] mt-7 max-w-xl mx-auto">
            10,000명 이상의 중년 회원이 매일 따뜻한 글을 나누고 있습니다.
            <br className="hidden sm:block" />
            나의 한 줄이 누군가에게 위로가 되는 곳, 뉴커플링.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
            <Link
              to="/community"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-stone-900 font-bold text-[15px] hover:bg-stone-100 transition-colors shadow-lg"
              data-testid="final-cta-primary"
            >
              내 이야기 시작하기
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/members"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold text-[15px] hover:bg-white/20 transition-colors"
              data-testid="final-cta-secondary"
            >
              회원 둘러보기
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-stone-400 text-xs">
            <span>· 본인 인증 완료된 회원만</span>
            <span>· 무료 가입 / 정회원 업그레이드 선택</span>
            <span>· 24시간 운영팀 모니터링</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
